'use client';

import { bidderListAddBidderModalAtom } from '@/atoms/bidderList/bidderListAddBidderModalAtom';
import { isSecretMenuModalShownAtom, isWinnerColumnsModalShownAtom } from './atom';
import { selectedTierForModalAtom } from '@/atoms/auction/approvalCriteriaAtoms';
import { showAddLotModalAtom, showBulkImportModalAtom } from '@/atoms/auction/catalogAtom';
import {
    showAuctionDeleteBlockedModalAtom,
    showAuctionDeletionConfirmModalAtom,
} from '@/atoms/auction/auctionDeletionAtoms';
import { showBulkLotDeletionConfirmModalAtom, showLotDeletionConfirmModalAtom } from '@/atoms/lot/lotDeletionAtoms';
import { showBulkLotRenumberModalAtom } from '@/atoms/lot/lotRenumberAtoms';
import { showCancelUploadModalAtom, showDuplicateFilesModalAtom } from '@/atoms/bulkPhoto/bulkPhotoAtoms';
import { showMovePhotosModalAtom } from '@/atoms/managePhotos/managePhotosModalAtoms';
import { showSaveAsTemplateModalAtom } from '@/atoms/template/auctionTemplateAtoms';
import { showTemplateDeletionConfirmModalAtom } from '@/atoms/template/templateDeletionAtoms';
import { uploadFailureModalAtom } from '@/atoms/lot/uploadFailureModalAtoms';
import { useAtomValue } from 'jotai';
import { useSecretMenuShortcut } from '@/hooks/useSecretMenuShortcut/useSecretMenuShortcut';
import dynamic from 'next/dynamic';

const AddBidderModal = dynamic(
    () =>
        import('@/components/Modals/AddBidderModal/AddBidderModal').then((mod) => ({
            default: mod.AddBidderModal,
        })),
    { ssr: false }
);

const SecretMenuModal = dynamic(
    () => import('@/components/Modals/SecretMenu/SecretMenu').then((mod) => mod.SecretMenuModal),
    {
        ssr: false,
    }
);

const AuctionDeleteConfirmationModal = dynamic(
    () =>
        import('@/components/Modals/AuctionDeleteConfirmationModal/AuctionDeleteConfirmationModal').then((mod) => ({
            default: mod.AuctionDeleteConfirmationModal,
        })),
    {
        ssr: false,
    }
);

const AuctionDeleteBlockedModal = dynamic(
    () =>
        import('@/components/Modals/AuctionDeleteBlockedModal/AuctionDeleteBlockedModal').then((mod) => ({
            default: mod.AuctionDeleteBlockedModal,
        })),
    {
        ssr: false,
    }
);

const AddLotModal = dynamic(
    () => import('@/components/Modals/AddLotModal/AddLotModal').then((mod) => ({ default: mod.default })),
    {
        ssr: false,
    }
);

const BulkImportModal = dynamic(
    () =>
        import('@/components/BulkImport/BulkImportModal/BulkImportModal').then((mod) => ({
            default: mod.BulkImportModal,
        })),
    {
        ssr: false,
    }
);

const BulkLotDeleteConfirmationModal = dynamic(
    () =>
        import('@/components/Modals/BulkLotDeleteConfirmationModal/BulkLotDeleteConfirmationModal').then((mod) => ({
            default: mod.BulkLotDeleteConfirmationModal,
        })),
    { ssr: false }
);

const BulkLotRenumberModal = dynamic(
    () =>
        import('@/components/Modals/BulkLotRenumberModal/BulkLotRenumberModal').then((mod) => ({
            default: mod.BulkLotRenumberModal,
        })),
    { ssr: false }
);

const LotDeleteConfirmationModal = dynamic(
    () =>
        import('@/components/Modals/LotDeleteConfirmationModal/LotDeleteConfirmationModal').then((mod) => ({
            default: mod.LotDeleteConfirmationModal,
        })),
    {
        ssr: false,
    }
);

const MovePhotosModal = dynamic(
    () =>
        import('@/components/ManagePhotos/MovePhotosModal/MovePhotosModal').then((mod) => ({
            default: mod.MovePhotosModal,
        })),
    {
        ssr: false,
    }
);

const DuplicateFilesModal = dynamic(
    () =>
        import('@/components/Modals/DuplicateFilesModal/DuplicateFilesModal').then((mod) => ({
            default: mod.DuplicateFilesModal,
        })),
    {
        ssr: false,
    }
);

const CancelUploadModal = dynamic(
    () =>
        import('@/components/Modals/CancelUploadModal/CancelUploadModal').then((mod) => ({
            default: mod.CancelUploadModal,
        })),
    {
        ssr: false,
    }
);

const WinnerColumnsModal = dynamic(
    () =>
        import('@/components/Modals/WinnerColumnsModal/WinnerColumnsModal').then((mod) => ({
            default: mod.WinnerColumnsModal,
        })),
    {
        ssr: false,
    }
);

const SaveAsTemplateModal = dynamic(
    () =>
        import('@/components/Modals/SaveAsTemplateModal/SaveAsTemplateModal').then((mod) => ({
            default: mod.SaveAsTemplateModal,
        })),
    {
        ssr: false,
    }
);

const UploadFailureModal = dynamic(
    () =>
        import('@/components/Modals/UploadFailureModal/UploadFailureModal').then((mod) => ({
            default: mod.UploadFailureModal,
        })),
    {
        ssr: false,
    }
);

const TemplateDeleteConfirmationModal = dynamic(
    () =>
        import('@/components/Modals/TemplateDeleteConfirmationModal/TemplateDeleteConfirmationModal').then((mod) => ({
            default: mod.TemplateDeleteConfirmationModal,
        })),
    {
        ssr: false,
    }
);

const TierSelectionModal = dynamic(
    () =>
        import('@/components/Modals/TierSelectionModal/TierSelectionModal').then((mod) => ({
            default: mod.TierSelectionModal,
        })),
    {
        ssr: false,
    }
);

export const Modals = () => {
    const isSecretMenuModalOpen = useAtomValue(isSecretMenuModalShownAtom);
    const showAuctionDeleteBlockedModal = useAtomValue(showAuctionDeleteBlockedModalAtom);
    const showAuctionDeletionConfirmModal = useAtomValue(showAuctionDeletionConfirmModalAtom);
    const showAddLotModal = useAtomValue(showAddLotModalAtom);
    const bulkImportAuctionGuid = useAtomValue(showBulkImportModalAtom);
    const showBulkLotDeletionConfirmModal = useAtomValue(showBulkLotDeletionConfirmModalAtom);
    const showBulkLotRenumberModal = useAtomValue(showBulkLotRenumberModalAtom);
    const showLotDeletionConfirmModal = useAtomValue(showLotDeletionConfirmModalAtom);
    const showMovePhotosModal = useAtomValue(showMovePhotosModalAtom);
    const showDuplicateFilesModal = useAtomValue(showDuplicateFilesModalAtom);
    const showCancelUploadModal = useAtomValue(showCancelUploadModalAtom);
    const isWinnerColumnsModalOpen = useAtomValue(isWinnerColumnsModalShownAtom);
    const uploadFailureModal = useAtomValue(uploadFailureModalAtom);
    const showAddBidderModal = useAtomValue(bidderListAddBidderModalAtom);
    const selectedTierForModal = useAtomValue(selectedTierForModalAtom);
    const showSaveAsTemplateModal = useAtomValue(showSaveAsTemplateModalAtom);
    const showTemplateDeletionConfirmModal = useAtomValue(showTemplateDeletionConfirmModalAtom);

    useSecretMenuShortcut();

    return (
        <>
            {isSecretMenuModalOpen && <SecretMenuModal />}
            {showAuctionDeleteBlockedModal && <AuctionDeleteBlockedModal />}
            {showAuctionDeletionConfirmModal && <AuctionDeleteConfirmationModal />}
            {showAddLotModal && <AddLotModal />}
            {bulkImportAuctionGuid && <BulkImportModal auctionId={bulkImportAuctionGuid} />}
            {showBulkLotDeletionConfirmModal !== null && <BulkLotDeleteConfirmationModal />}
            {showBulkLotRenumberModal && <BulkLotRenumberModal />}
            {showLotDeletionConfirmModal && <LotDeleteConfirmationModal />}
            {showMovePhotosModal && <MovePhotosModal />}
            {showDuplicateFilesModal && <DuplicateFilesModal />}
            {showCancelUploadModal && <CancelUploadModal />}
            {isWinnerColumnsModalOpen && <WinnerColumnsModal />}
            {uploadFailureModal.show && <UploadFailureModal />}
            {showAddBidderModal !== null && <AddBidderModal />}
            {selectedTierForModal !== null && <TierSelectionModal />}
            {showSaveAsTemplateModal && <SaveAsTemplateModal />}
            {showTemplateDeletionConfirmModal && <TemplateDeleteConfirmationModal />}
        </>
    );
};
